import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import { detectBlankPdfPages, removeBlankPdfPages, removePdfPages } from './removePagesPdfEngine';
import { formatPageSelection, getPdfPageCount, parsePageSelection } from '../split/splitPdfEngine';
import {
    applyPdfPageEdits,
    MAX_EDIT_PAGES_LARGE,
} from '../shared/pdfPageEditEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import PdfPageEditor from '../../components/pdf/PdfPageEditor';
import { usePdfPageEdits, fileKeyOf } from '../../components/pdf/usePdfPageEdits';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import ToolCard from '../../components/common/ToolCard';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 500 * 1024 * 1024;

export default function RemovePagesPdf() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [pageCount, setPageCount] = useState(null);
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [mode, setMode] = useState('manual');
    const [pageInput, setPageInput] = useState('');
    const [detecting, setDetecting] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const clearResult = useCallback(() => {
        setResult(null);
        setError(null);
        setProgress(null);
    }, []);

    const editFiles = useMemo(() => (file ? [file] : []), [file]);
    const { pagesByKey, updatePagesForKey, resetPageEdits } = usePdfPageEdits(editFiles, clearResult, MAX_EDIT_PAGES_LARGE);
    const pages = file ? pagesByKey[fileKeyOf(file)] : null;
    const editable = Array.isArray(pages);
    const removedCount = editable ? pages.filter((p) => p.removed).length : 0;

    // Keep the left-side page input in sync with pages marked removed in the preview grid.
    useEffect(() => {
        if (!editable) return;
        const numbers = pages
            .filter((p) => p.removed)
            .map((p) => p.sourcePageNumber ?? (p.pageIndex + 1));
        setPageInput(formatPageSelection(numbers));
    }, [editable, pages]);

    const loadFile = useCallback(async (incoming) => {
        setError(null);
        if (!incoming) return;
        if (!(incoming.type === 'application/pdf' || incoming.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('removePagesPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (incoming.size > MAX_FILE_BYTES) {
            const msg = t('removePagesPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        setLoadingMeta(true);
        setFile(incoming);
        setResult(null);
        setPageInput('');
        try {
            const count = await getPdfPageCount(incoming);
            setPageCount(count);
        } catch (err) {
            setFile(null);
            setPageCount(null);
            const msg = err?.message === 'password'
                ? t('removePagesPdf.errors.password')
                : t('removePagesPdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setLoadingMeta(false);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in. This tool
    // holds one file, so a paste of several takes the first.
    usePdfPaste((files) => loadFile(files[0]));

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) loadFile(dropped);
    };

    const clearFile = () => {
        setFile(null);
        setPageCount(null);
        setPageInput('');
        setResult(null);
        setError(null);
        resetPageEdits();
    };

    // Editor quick action: mark the typed pages (original page numbers) as removed.
    const markRangeRemoved = () => {
        if (!editable || !pageInput.trim() || !pageCount) return;
        try {
            const indices = new Set(parsePageSelection(pageInput, pageCount));
            updatePagesForKey(fileKeyOf(file), (prev) => prev.map((p) => (
                indices.has(p.pageIndex) ? { ...p, removed: true } : p
            )));
        } catch {
            const msg = t('removePagesPdf.errors.invalidSelection', { total: toBnDigits(pageCount) });
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        }
    };

    // Editor quick action: detect blank pages and mark them removed.
    const detectBlank = async () => {
        if (!editable || detecting) return;
        setDetecting(true);
        setError(null);
        try {
            const blanks = await detectBlankPdfPages(file);
            if (!blanks.length) {
                notifications.show({ title: t('common.warning'), message: t('removePagesPdf.errors.noBlank'), color: 'yellow' });
                return;
            }
            const blankSet = new Set(blanks);
            updatePagesForKey(fileKeyOf(file), (prev) => prev.map((p) => (
                blankSet.has(p.pageIndex) ? { ...p, removed: true } : p
            )));
        } catch (err) {
            const msg = err?.message === 'password'
                ? t('removePagesPdf.errors.password')
                : t('removePagesPdf.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setDetecting(false);
        }
    };

    const handleRemove = async () => {
        if (!file || !pageCount || processing) return;

        if (editable && removedCount === 0) {
            const msg = t('removePagesPdf.errors.emptySelection');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (!editable && mode === 'manual' && !pageInput.trim()) return;

        recordToolUsage('pdf/remove-pages', { category: 'pdf' });

        setProcessing(true);
        setResult(null);
        setError(null);

        try {
            const base = file.name.replace(/\.pdf$/i, '');
            let blob;
            let removed = 0;
            let total = pageCount;

            if (editable) {
                if (removedCount >= pages.length) throw new Error('removeAll');
                blob = await applyPdfPageEdits(file, pages);
                removed = removedCount;
                total = pages.length;
            } else if (mode === 'blank') {
                const out = await removeBlankPdfPages(file, (page, tot) => setProgress({ page, total: tot }));
                blob = out.blob;
                removed = out.removedCount;
            } else {
                blob = await removePdfPages(file, pageInput, pageCount);
                removed = parsePageSelection(pageInput, pageCount).length;
            }

            setResult({ name: `${base}-trimmed.pdf`, blob, removedCount: removed, pageCount: total });
            notifications.show({ title: t('common.success'), message: t('removePagesPdf.done'), color: 'green' });
        } catch (err) {
            let msg = t('removePagesPdf.errors.failed');
            if (err?.message === 'password') msg = t('removePagesPdf.errors.password');
            else if (err?.message === 'empty') msg = t('removePagesPdf.errors.emptySelection');
            else if (err?.message === 'invalid' || err?.message === 'outOfRange') {
                msg = t('removePagesPdf.errors.invalidSelection', { total: toBnDigits(pageCount) });
            } else if (err?.message === 'removeAll') msg = t('removePagesPdf.errors.removeAll');
            else if (err?.message === 'noBlank') msg = t('removePagesPdf.errors.noBlank');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const canRemove = file && pageCount && !loadingMeta && !processing && (
        editable
            ? removedCount > 0
            : (mode === 'blank' || !!pageInput.trim())
    );

    const progressLabel = progress
        ? t('removePagesPdf.progress', {
            page: toBnDigits(progress.page),
            pages: toBnDigits(progress.total),
        })
        : '';

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!canRemove,
        processing,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => genericHowToSteps(t), [t]);

    const resultMeta = result
        ? t('removePagesPdf.resultMeta', {
            removed: toBnDigits(result.removedCount),
            remaining: toBnDigits(result.pageCount - result.removedCount),
            size: formatFileSize(result.blob.size),
        })
        : undefined;
    const statusText = getToolStatusText(status, t, {
        success: t('removePagesPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });
    const progressPercent = progress?.total
        ? Math.round((progress.page / progress.total) * 100)
        : undefined;
    const fileMeta = file
        ? [
            formatFileSize(file.size),
            pageCount != null && !loadingMeta ? t('removePagesPdf.pageCount', { count: toBnDigits(pageCount) }) : null,
            loadingMeta ? t('removePagesPdf.reading') : null,
        ].filter(Boolean).join(' · ')
        : '';

    return (
        <ToolPageLayout
            slug="pdf/remove-pages"
            subtitleKey="removePagesPdf.subtitle"
            status={status}
            statusText={statusText}
            controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
            showAdd={!file && !loadingMeta}
            onAdd={() => fileInputRef.current?.click()}
            howToSteps={howToSteps}
            onReset={file || result || error ? clearFile : undefined}
            matchSidebarHeight={!!file && !result}
            matchSidebarMinHeight="min(780px, 82vh)"
            primaryAction={{
                label: t('removePagesPdf.remove'),
                onClick: handleRemove,
                disabled: !canRemove || !!result,
                loading: processing,
            }}
            controls={(
                <>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        onChange={(e) => { loadFile(e.target.files?.[0]); e.target.value = ''; }}
                    />

                    {!file ? (
                        <PdfDropZone
                            title={t('removePagesPdf.dropTitle')}
                            hint={t('removePagesPdf.dropHint')}
                            isDragging={isDragging}
                            onClick={() => fileInputRef.current?.click()}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    ) : (
                        <PdfUploadedFile
                            name={file.name}
                            meta={fileMeta}
                            onRemove={clearFile}
                            removeLabel={t('removePagesPdf.removeFile')}
                        />
                    )}
                </>
            )}
            settings={file && pageCount != null && !loadingMeta ? (
                <ToolCard title={t('removePagesPdf.modeTitle')}>
                    {editable ? (
                        <div className="space-y-3">
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">{t('removePagesPdf.editHint')}</p>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={pageInput}
                                    onChange={(e) => { setPageInput(e.target.value); setError(null); }}
                                    placeholder={t('removePagesPdf.pagesPlaceholder')}
                                    className="min-w-0 flex-1 rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                />
                                <button
                                    type="button"
                                    onClick={markRangeRemoved}
                                    disabled={!pageInput.trim()}
                                    className="flex-none rounded-lg border border-zinc-200 px-3 py-2 text-[12.5px] font-semibold text-zinc-600 transition-colors hover:border-primary/40 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-300"
                                >
                                    {t('removePagesPdf.markRemove')}
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={detectBlank}
                                disabled={detecting}
                                className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] font-semibold text-zinc-600 transition-colors hover:border-primary/40 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-300"
                            >
                                {detecting ? t('removePagesPdf.detecting') : t('removePagesPdf.detectBlank')}
                            </button>
                            <p className="text-xs text-primary">
                                {t('removePagesPdf.markedCount', { count: toBnDigits(removedCount) })}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setMode('manual'); setError(null); setResult(null); }}
                                    className={`rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        mode === 'manual'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {t('removePagesPdf.modeManual')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setMode('blank'); setError(null); setResult(null); }}
                                    className={`rounded-lg border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                                        mode === 'blank'
                                            ? 'border-primary bg-primary/10 text-primary'
                                            : 'border-zinc-200 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300'
                                    }`}
                                >
                                    {t('removePagesPdf.modeBlank')}
                                </button>
                            </div>

                            {mode === 'manual' ? (
                                <div className="space-y-1.5">
                                    <label htmlFor="remove-pages" className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                        {t('removePagesPdf.pagesLabel')}
                                    </label>
                                    <input
                                        id="remove-pages"
                                        type="text"
                                        value={pageInput}
                                        onChange={(e) => { setPageInput(e.target.value); setError(null); setResult(null); }}
                                        placeholder={t('removePagesPdf.pagesPlaceholder')}
                                        className="w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-2.5 text-sm text-zinc-900 outline-none placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                                    />
                                    <p className="text-xs text-zinc-400">{t('removePagesPdf.pagesHint', { total: toBnDigits(pageCount) })}</p>
                                </div>
                            ) : (
                                <p className="text-xs text-zinc-400">{t('removePagesPdf.blankHint')}</p>
                            )}
                        </div>
                    )}
                </ToolCard>
            ) : null}
            preview={(
                <ToolPreviewPanel
                    title={t('toolPage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('toolPage.previewEmpty')}
                    idleHint={t('toolPage.previewEmptyHint')}
                    processingLabel={progressLabel || t('removePagesPdf.removing')}
                    progress={progressPercent}
                    error={error}
                    successLabel={resultMeta}
                    downloadLabel={t('removePagesPdf.download')}
                    onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                    onPrint={result ? () => printBlob(result.blob) : undefined}
                    onEditAgain={result ? clearResult : undefined}
                    editAgainLabel={t('toolPage.editAgain')}
                    onAgain={result ? clearFile : undefined}
                    againLabel={t('removePagesPdf.startOver')}
                    readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('removePagesPdf.remove')} /> : null}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                    showIdle={!file && !error}
                    pdfPreview={!!file && !processing && !error}
                >
                    {file && !processing && !error ? (
                        result ? (
                            <PdfDocPreview source={result.blob} />
                        ) : (
                            <PdfPageEditor
                                files={editFiles}
                                pagesByKey={pagesByKey}
                                onPagesChangeForKey={updatePagesForKey}
                                activeIndex={0}
                                onActiveIndexChange={() => {}}
                            />
                        )
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
    );
}
